// hooks/academicPlan/useCourseOperations.ts
/**
 * Course operations hook
 * Manages adding, removing, moving, and reordering courses
 */
import { useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../useToast';
import { getErrorMessage } from '../../utils/errorUtils';
import { useConfirm } from '../../contexts/ConfirmContext';
import {
  cleanCourseForUI as cleanCourseForUIRaw,
  extractPlaceholderData,
  logDecisionToProfile
} from './helpers';
import { dispatchDegreeAuditRefresh } from '../../utils/appEvents';
import { PlanActionType } from '../../adapters/plan';
import type { AcademicPlan, SemesterCode, EnrolledCourse } from '../../types';
import type { AddCoursesItem, AddCoursesResult } from '../../services/typedApiClient';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Course input type - either a full course or minimal course data
 * This represents incoming course data that may come from various sources
 * (search results, drag-drop, etc.) and will be converted to EnrolledCourse
 */
export interface CourseInput {
  code: string;
  course_code?: string;
  title?: string;
  course_title?: string;
  credits?: number;
  status?: string;
  isPlaceholder?: boolean;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Wrap cleanCourseForUI to ensure status field and return EnrolledCourse
 */
const cleanCourseForUI = (course: CourseInput): EnrolledCourse => {
  const cleaned = cleanCourseForUIRaw(course);
  if (!cleaned) {
    // Fallback for null case (shouldn't happen with valid input)
    return {
      code: course.code,
      credits: course.credits ?? 0,
      status: 'planned'
    };
  }
  return {
    ...cleaned,
    status: (course.status as EnrolledCourse['status']) || 'planned',
  } as EnrolledCourse;
};

/**
 * Parameters for the useCourseOperations hook
 */
export interface UseCourseOperationsParams {
  academicPlan: AcademicPlan;
  setAcademicPlan: React.Dispatch<React.SetStateAction<AcademicPlan>>;
  currentPlanId: number | null;
  savePlanToBackend: (planId: number, action: string, data: Record<string, unknown>) => Promise<unknown>;
  refreshPlan: () => Promise<void>;
}

/**
 * Return type for the useCourseOperations hook
 */
export interface CourseOperations {
  addCourse: (course: CourseInput, semester: SemesterCode) => Promise<boolean>;
  addCourses: (items: AddCoursesItem[], semester: SemesterCode) => Promise<AddCoursesResult | null>;
  reorderCourse: (semesterKey: SemesterCode, fromIndex: number, toIndex: number) => Promise<void>;
  addCourseAtPosition: (course: CourseInput, semesterKey: SemesterCode, position: number) => Promise<boolean>;
  removeCourse: (semester: SemesterCode, courseIndex: number, skipConfirmation?: boolean) => Promise<void>;
  moveCourse: (course: CourseInput, fromSemester: SemesterCode, toSemester: SemesterCode, fromIndex: number) => Promise<void>;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook for course operations within an academic plan
 */
export const useCourseOperations = ({
  academicPlan,
  setAcademicPlan,
  currentPlanId,
  savePlanToBackend,
  refreshPlan
}: UseCourseOperationsParams): CourseOperations => {
  const { user } = useAuth();
  const { showError, showSuccess, showWarning } = useToast();
  const { confirm } = useConfirm();

  // Guard to prevent duplicate reorder operations (e.g., from React StrictMode)
  const reorderInProgressRef = useRef<string | null>(null);

  // Add course to a specific semester
  // Returns true when the course was added, false on any no-op/failure. Callers
  // that move a card (drag-and-drop) MUST gate the source removal on this so a
  // rejected add never deletes the only copy.
  const addCourse = useCallback(async (course: CourseInput, semester: SemesterCode): Promise<boolean> => {
    // Validation
    if (!currentPlanId) {
      showError('No academic plan selected. Please refresh the page.');
      return false;
    }

    if (!academicPlan) {
      showError('Academic plan not loaded. Please refresh the page.');
      return false;
    }

    if (!course || !course.code) {
      showError('Invalid course data. Please try again.');
      return false;
    }

    if (!semester) {
      showError('Please specify a semester for this course.');
      return false;
    }

    // Placeholder courses persist server-side (#205) — optimistic update,
    // then the add_placeholder action; revert if the backend rejects it.
    if (course.isPlaceholder) {
      setAcademicPlan(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          semesters: {
            ...prev.semesters,
            [semester]: [...(prev.semesters[semester] || []), course as AcademicPlan['semesters'][string][number]]
          }
        };
      });
      try {
        await savePlanToBackend(currentPlanId, PlanActionType.AddPlaceholder, {
          placeholder: extractPlaceholderData(course),
          semester: semester
        });
        return true;
      } catch (err) {
        setAcademicPlan(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            semesters: {
              ...prev.semesters,
              [semester]: (prev.semesters[semester] || []).filter(c => c.code !== course.code)
            }
          };
        });
        showError(`Failed to save placeholder: ${getErrorMessage(err)}`);
        return false;
      }
    }

    // Clean the course object before optimistic update to prevent "[object Object]"
    const cleanCourse = cleanCourseForUI(course);

    // Optimistically update UI with CLEAN course data
    setAcademicPlan(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        semesters: {
          ...prev.semesters,
          [semester]: [...(prev.semesters[semester] || []), cleanCourse]
        }
      };
    });

    try {
      // Send to backend - semester should be in numeric format
      const result = await savePlanToBackend(currentPlanId, PlanActionType.AddCourse, {
        course_code: course.code,
        semester: semester
      }) as Record<string, unknown> | undefined;
      showSuccess(`${course.code} added successfully`);

      // Show cross-listing warning if the backend detected an equivalent already in the plan
      const clWarning = result?.cross_listing_warning as { message?: string } | undefined;
      if (clWarning?.message) {
        showWarning(clWarning.message, { duration: 8000 });
      }

      // V2: Log decision to student profile (fire-and-forget)
      logDecisionToProfile(user?.id, {
        type: 'added',
        course: course.code,
        semester: semester,
      });

      // Trigger degree audit refresh after successful course addition
      dispatchDegreeAuditRefresh(currentPlanId);

      // Refresh plan to pick up updated optimization status (stale detection)
      await refreshPlan();
      return true;
    } catch (err) {
      const error = err as Error & { isCourseNotFound?: boolean };

      // Revert on error
      setAcademicPlan(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          semesters: {
            ...prev.semesters,
            [semester]: prev.semesters[semester].filter(c => c.code !== course.code)
          }
        };
      });

      // Show user-friendly error with toast
      if (error.message.includes('not found') || error.isCourseNotFound) {
        showError(`Course ${course.code} is not available in the course database`);
      } else if (error.message.includes('already exists')) {
        showWarning(`${course.code} is already in this semester`);
      } else {
        showError(`Failed to add ${course.code}: ${error.message}`);
      }

      return false;
    }
  }, [currentPlanId, academicPlan, setAcademicPlan, savePlanToBackend, refreshPlan, showError, showSuccess, showWarning, user?.id]);

  // Add several courses to one semester in one request (the add-course
  // modal's multi-select). The backend keeps the courses that succeed and
  // reports the rest per course, so this returns the result for the caller to
  // show rather than toasting itself. No optimistic update: a partial result
  // would need per-course reverts, and the single refresh below is the truth.
  // Throws if the request itself fails (after refreshing — see below).
  const addCourses = useCallback(async (
    items: AddCoursesItem[],
    semester: SemesterCode,
  ): Promise<AddCoursesResult | null> => {
    if (!currentPlanId) {
      showError('No academic plan selected. Please refresh the page.');
      return null;
    }
    if (items.length === 0) return null;

    let result: AddCoursesResult;
    try {
      result = await savePlanToBackend(currentPlanId, PlanActionType.AddCourses, {
        semester,
        courses: items,
      }) as AddCoursesResult;
    } catch (err) {
      // Each course commits on its own, so a request that failed partway may
      // still have added some. Refresh so the grid shows what is really there.
      dispatchDegreeAuditRefresh(currentPlanId);
      await refreshPlan().catch(() => undefined);
      throw err;
    }

    if (result.added > 0) {
      for (const entry of result.results) {
        if (entry.status === 'added') {
          logDecisionToProfile(user?.id, { type: 'added', course: entry.course_code, semester });
        }
      }
      dispatchDegreeAuditRefresh(currentPlanId);
      await refreshPlan();
    }
    return result;
  }, [currentPlanId, savePlanToBackend, refreshPlan, showError, user?.id]);

  // Reorder course within the same semester
  const reorderCourse = useCallback(async (semesterKey: SemesterCode, fromIndex: number, toIndex: number): Promise<void> => {
    if (fromIndex === toIndex) return; // No change needed
    if (!academicPlan) return;

    // Deduplication guard to prevent duplicate operations (e.g., React StrictMode)
    const reorderKey = `${semesterKey}-${fromIndex}-${toIndex}-${Date.now()}`;
    if (reorderInProgressRef.current) {
      return;
    }
    reorderInProgressRef.current = reorderKey;

    try {
      // Get the course being moved BEFORE the optimistic update
      const courseToMove = academicPlan.semesters[semesterKey]?.[fromIndex];

      if (!courseToMove) {
        console.error('Course not found at index', fromIndex);
        return;
      }

      // Perform the reorder operation outside React's state cycle first
      const originalCourses = [...(academicPlan.semesters[semesterKey] || [])];
      const [movedCourse] = originalCourses.splice(fromIndex, 1);
      originalCourses.splice(toIndex, 0, movedCourse);

      // Simple state update with the pre-calculated result
      setAcademicPlan(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          semesters: {
            ...prev.semesters,
            [semesterKey]: originalCourses
          }
        };
      });

      // Backend update - only for real courses
      if (courseToMove && !courseToMove.isPlaceholder) {
        await savePlanToBackend(currentPlanId!, PlanActionType.ReorderCourse, {
          semester: semesterKey,
          from_index: fromIndex,
          to_index: toIndex
        });

        // Trigger degree audit refresh after successful reorder
        dispatchDegreeAuditRefresh(currentPlanId!);
      }
    } catch (error) {
      const err = error as Error;
      console.error('Failed to reorder course:', err);
      // Revert optimistic update by refreshing from backend
      await refreshPlan();
      showError(`Failed to reorder course: ${err.message}`);
      throw error;
    } finally {
      // Clear the guard
      reorderInProgressRef.current = null;
    }
  }, [academicPlan, setAcademicPlan, currentPlanId, savePlanToBackend, refreshPlan, showError]);

  // Add course at specific position in semester. Returns true when added, false
  // on any no-op (validation, duplicate). Drag-and-drop gates source removal on
  // this so a rejected add never deletes the dragged card.
  const addCourseAtPosition = useCallback(async (course: CourseInput, semesterKey: SemesterCode, position: number): Promise<boolean> => {
    try {
      // Validation
      if (!currentPlanId) {
        showError('No academic plan selected. Please refresh the page.');
        return false;
      }

      if (!course || !course.code) {
        showError('Invalid course data. Please try again.');
        return false;
      }

      if (!semesterKey) {
        showError('Please specify a semester for this course.');
        return false;
      }

      if (!academicPlan) {
        showError('Academic plan not loaded. Please refresh the page.');
        return false;
      }

      // Check if course already exists in this semester
      const existingCourses = academicPlan.semesters[semesterKey] || [];
      const courseExists = existingCourses.some(c => c.code === course.code);

      if (courseExists) {
        showWarning(`${course.code} is already in this semester`);
        return false; // Don't add if already exists
      }

      // Placeholder courses persist server-side (#205) — optimistic insert
      // at position, then the add_placeholder action; revert on rejection.
      if (course.isPlaceholder) {
        setAcademicPlan(prev => {
          if (!prev) return prev;
          const newPlan = { ...prev };
          const courses = [...(newPlan.semesters[semesterKey] || [])];

          // Check if placeholder already exists (React StrictMode protection)
          if (courses.some(c => c.code === course.code)) {
            return prev;
          }

          courses.splice(position, 0, course as AcademicPlan['semesters'][string][number]);
          newPlan.semesters = { ...newPlan.semesters, [semesterKey]: courses };
          return newPlan;
        });
        try {
          await savePlanToBackend(currentPlanId, PlanActionType.AddPlaceholder, {
            placeholder: extractPlaceholderData(course),
            semester: semesterKey,
            position: position
          });
          return true;
        } catch (err) {
          setAcademicPlan(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              semesters: {
                ...prev.semesters,
                [semesterKey]: (prev.semesters[semesterKey] || []).filter(c => c.code !== course.code)
              }
            };
          });
          showError(`Failed to save placeholder: ${getErrorMessage(err)}`);
          return false;
        }
      }

      // course.code is guaranteed to exist due to validation above
      const courseCode = course.code;
      const cleanCourse = cleanCourseForUI(course);

      // Optimistic update with React StrictMode protection
      setAcademicPlan(prev => {
        if (!prev) return prev;
        const newPlan = { ...prev };
        const courses = [...(newPlan.semesters[semesterKey] || [])];

        // IDEMPOTENCY CHECK: Prevent React StrictMode double-execution
        // Check if course already exists at the target position
        if (courses[position] && courses[position].code === courseCode) {
          return prev; // Return unchanged state
        }

        // Check if course exists anywhere in the semester
        if (courses.some(c => c.code === courseCode)) {
          return prev; // Return unchanged state
        }

        // Insert course at specified position
        courses.splice(position, 0, cleanCourse);

        newPlan.semesters = { ...newPlan.semesters, [semesterKey]: courses };
        return newPlan;
      });

      // Backend update
      const result = await savePlanToBackend(currentPlanId, PlanActionType.AddCourseAtPosition, {
        course_code: courseCode,
        semester: semesterKey,
        position: position
      }) as Record<string, unknown> | undefined;

      showSuccess(`${courseCode} added successfully`);

      // Show cross-listing warning if the backend detected an equivalent already in the plan
      const clWarning = result?.cross_listing_warning as { message?: string } | undefined;
      if (clWarning?.message) {
        showWarning(clWarning.message, { duration: 8000 });
      }

      // V2: Log decision to student profile (fire-and-forget)
      logDecisionToProfile(user?.id, {
        type: 'added',
        course: courseCode,
        semester: semesterKey,
      });

      // Trigger degree audit refresh after successful course addition
      dispatchDegreeAuditRefresh(currentPlanId);

      return true;
    } catch (error) {
      const err = error as Error & { isCourseNotFound?: boolean };
      console.error('Failed to add course at position:', err);

      // Revert optimistic update
      await refreshPlan();

      // Show user-friendly error with toast
      if (err.message.includes('not found') || err.isCourseNotFound) {
        showError(`Course ${course.code} is not available in the course database`);
      } else if (err.message.includes('already exists')) {
        showWarning(`${course.code} is already in this semester`);
      } else {
        showError(`Failed to add ${course.code}: ${err.message}`);
      }

      throw error;
    }
  }, [currentPlanId, academicPlan, setAcademicPlan, savePlanToBackend, refreshPlan, showError, showSuccess, showWarning, user?.id]);

  // Remove course from a semester
  const removeCourse = useCallback(async (semester: SemesterCode, courseIndex: number, skipConfirmation = false): Promise<void> => {
    if (!currentPlanId || !academicPlan) return;

    const courseToRemove = academicPlan.semesters[semester][courseIndex];

    // If it's a placeholder and not skipping confirmation, confirm deletion
    if (courseToRemove?.isPlaceholder && !skipConfirmation) {
      const confirmDelete = await confirm({
        title: 'Delete Placeholder',
        message: `Delete placeholder "${courseToRemove.title}"? This cannot be undone.`,
        confirmText: 'Delete',
        variant: 'danger'
      });
      if (!confirmDelete) return;
    }

    // Placeholder removal persists server-side (#205) — optimistic remove,
    // then the remove_placeholder action (idempotent); revert on failure.
    if (courseToRemove?.isPlaceholder) {
      setAcademicPlan(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          semesters: {
            ...prev.semesters,
            [semester]: prev.semesters[semester].filter((_, index) => index !== courseIndex)
          }
        };
      });
      try {
        await savePlanToBackend(currentPlanId, PlanActionType.RemovePlaceholder, {
          placeholder_code: courseToRemove.code
        });
      } catch (err) {
        setAcademicPlan(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            semesters: {
              ...prev.semesters,
              [semester]: [
                ...prev.semesters[semester].slice(0, courseIndex),
                courseToRemove,
                ...prev.semesters[semester].slice(courseIndex)
              ]
            }
          };
        });
        showError(`Failed to remove placeholder: ${getErrorMessage(err)}`);
      }
      return;
    }

    // Optimistically update UI
    setAcademicPlan(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        semesters: {
          ...prev.semesters,
          [semester]: prev.semesters[semester].filter((_, index) => index !== courseIndex)
        }
      };
    });

    try {
      await savePlanToBackend(currentPlanId, PlanActionType.RemoveCourse, {
        course_code: courseToRemove.code,
        semester: semester
      });
      showSuccess(`${courseToRemove.code} removed successfully`);

      // V2: Log decision to student profile (fire-and-forget)
      logDecisionToProfile(user?.id, {
        type: 'removed',
        course: courseToRemove.code,
        semester: semester,
      });

      // Trigger degree audit refresh after successful course removal
      dispatchDegreeAuditRefresh(currentPlanId);
    } catch (err) {
      // Revert on error
      setAcademicPlan(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          semesters: {
            ...prev.semesters,
            [semester]: [
              ...prev.semesters[semester].slice(0, courseIndex),
              courseToRemove,
              ...prev.semesters[semester].slice(courseIndex)
            ]
          }
        };
      });
      showError(`Failed to remove ${courseToRemove.code}: ${getErrorMessage(err)}`);
    }
  }, [currentPlanId, academicPlan, setAcademicPlan, savePlanToBackend, showError, showSuccess, user?.id, confirm]);

  // Move course between semesters
  const moveCourse = useCallback(async (course: CourseInput, fromSemester: SemesterCode, toSemester: SemesterCode, fromIndex: number): Promise<void> => {
    if (fromSemester === toSemester) return;

    // Placeholder moves persist server-side (#205) as ONE backend move
    // (not remove+add — that has a window where the remove landed and the
    // add didn't, the exact loss class persistence closes). Optimistic
    // update, refresh-to-truth on failure.
    if (course?.isPlaceholder) {
      setAcademicPlan(prev => {
        if (!prev) return prev;
        const updatedPlan = { ...prev };
        const updatedSemesters = { ...updatedPlan.semesters };

        // Remove from source
        updatedSemesters[fromSemester] = updatedSemesters[fromSemester].filter((_, index) => index !== fromIndex);

        // Add to target
        updatedSemesters[toSemester] = [...(updatedSemesters[toSemester] || []), course as AcademicPlan['semesters'][string][number]];

        return { ...updatedPlan, semesters: updatedSemesters };
      });
      try {
        await savePlanToBackend(currentPlanId!, PlanActionType.MoveCourse, {
          course_code: course.code,
          from_semester: fromSemester,
          to_semester: toSemester
        });
      } catch (err) {
        await refreshPlan();
        showError(`Failed to move placeholder: ${getErrorMessage(err)}`);
      }
      return;
    }

    // Use existing operations for real courses
    await removeCourse(fromSemester, fromIndex, true);
    await addCourse(course, toSemester);

    // V2: Log move decision to student profile (fire-and-forget)
    logDecisionToProfile(user?.id, {
      type: 'moved',
      course: course.code,
      from_semester: fromSemester,
      to_semester: toSemester,
    });
  }, [setAcademicPlan, removeCourse, addCourse, user?.id, currentPlanId, savePlanToBackend, refreshPlan, showError]);

  // Memoize operations object
  return useMemo(() => ({
    addCourse,
    addCourses,
    reorderCourse,
    addCourseAtPosition,
    removeCourse,
    moveCourse
  }), [addCourse, addCourses, reorderCourse, addCourseAtPosition, removeCourse, moveCourse]);
};
